import React, { useState } from 'react';
import {
  useListLearningGoalsQuery,
  useCreateLearningGoalMutation,
  useUpdateLearningGoalMutation,
} from '../../store/api/localApi';
import { LearningGoal, LearningGoalFramework, LearningGoalTheme } from '../../types';
import { useToast } from '../Toast';
import { THEME_LABELS, THEME_COLORS, THEME_ORDER, groupByThemeAndAim } from './themes';

interface Props {
  /** Organization whose learning goals are being administered. */
  organizationId: string;
  /**
   * Read-only view: hide the add-goal form and per-goal deactivate controls,
   * leaving just the goal catalogue. Used for STAFF, who may READ goals (they
   * tag observations against them) but not manage them. The backend enforces
   * this regardless; this only tidies the UI.
   */
  readOnly?: boolean;
}

// The admin picks a theme + an aim (by its real wording) and types the goal.
// aimNumber / goalIndex — the Aistéar outline's positional keys — are derived,
// never typed: the aim carries its number, and the goal index is the next free
// slot in that theme+aim (so two custom goals never collide on the sort key).
const EMPTY_FORM = {
  theme: 'WELLBEING' as LearningGoalTheme,
  aimNumber: null as number | null,
  text: '',
};

const EDITION_LABELS: Record<LearningGoalFramework, string> = {
  AISTEAR_2024: 'Aistéar 2024',
  AISTEAR_2009: 'Aistéar 2009',
  CUSTOM: 'Custom',
};

/**
 * The four theme cards + tap-to-expand drill-down, shared with the student
 * coverage view's look (LearningGoalCoverage) but count-based rather than
 * evidenced/total — a catalogue has no coverage. `onDeactivate` is passed only
 * for the custom section (official goals are immutable).
 */
const ThemeCards: React.FC<{
  goals: LearningGoal[];
  testIdPrefix: string;
  onDeactivate?: (goal: LearningGoal) => void;
}> = ({ goals, testIdPrefix, onDeactivate }) => {
  const [openTheme, setOpenTheme] = useState<LearningGoalTheme | null>(null);
  const groups = groupByThemeAndAim(goals);

  if (groups.length === 0) return null;

  return (
    <>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {groups.map((group) => {
          const themeGoals = group.aims.flatMap((a) => a.goals);
          const colors = THEME_COLORS[group.theme];
          const isOpen = openTheme === group.theme;
          return (
            <button
              key={group.theme}
              type="button"
              data-testid={`${testIdPrefix}-theme-card-${group.theme}`}
              aria-expanded={isOpen}
              onClick={() => setOpenTheme(isOpen ? null : group.theme)}
              className={`flex flex-col items-center rounded-lg border p-3 text-center transition ${
                isOpen
                  ? `${colors.bg} border-current ${colors.text}`
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              <span className={`text-2xl font-semibold ${isOpen ? colors.text : 'text-gray-700'}`}>
                {themeGoals.length}
              </span>
              <span
                className={`mt-1 text-xs font-medium ${isOpen ? colors.text : 'text-gray-600'}`}
              >
                {THEME_LABELS[group.theme]}
              </span>
            </button>
          );
        })}
      </div>

      {openTheme &&
        groups
          .filter((g) => g.theme === openTheme)
          .map((group) => (
            <div
              key={group.theme}
              data-testid={`${testIdPrefix}-theme-detail-${group.theme}`}
              className="mt-4 rounded-lg border border-gray-200 p-4"
            >
              <h4 className={`text-sm font-semibold ${THEME_COLORS[group.theme].text}`}>
                {THEME_LABELS[group.theme]}
              </h4>
              <div className="mt-2 space-y-3">
                {group.aims.map((aim) => (
                  <div key={aim.aimNumber}>
                    <p className="text-xs font-medium text-gray-500">{aim.aimText}</p>
                    <ul className="mt-1 space-y-1">
                      {aim.goals.map((goal) => (
                        <li
                          key={goal.id}
                          className="flex items-start justify-between gap-3 text-sm"
                        >
                          <span className="text-gray-900">{goal.text}</span>
                          {onDeactivate && (
                            <button
                              type="button"
                              data-testid={`lg-deactivate-${goal.id}`}
                              onClick={() => onDeactivate(goal)}
                              className="shrink-0 inline-flex items-center rounded-md bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                            >
                              Deactivate
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
    </>
  );
};

/**
 * LOCATION_ADMIN+ admin surface for learning goals, split into two clearly
 * separated sections so it's obvious what is official versus what the org
 * added:
 *   1. "Aistéar learning goals" — the official NCCA framework (edition badge),
 *      read-only (official goals are immutable).
 *   2. "Your custom goals" — the org's own goals, with the add form and
 *      deactivate controls.
 * Both use the same theme-card + drill-down treatment as the student coverage
 * view. Authorization is enforced server-side; this only tidies the UI.
 */
const CustomGoalsAdmin: React.FC<Props> = ({ organizationId, readOnly = false }) => {
  const { showSuccess, showError } = useToast();
  const { data, isLoading, isError } = useListLearningGoalsQuery(
    { organizationId },
    { skip: !organizationId }
  );
  const [createGoal, { isLoading: isCreating }] = useCreateLearningGoalMutation();
  const [updateGoal] = useUpdateLearningGoalMutation();

  const [form, setForm] = useState(EMPTY_FORM);

  const goals = data?.goals ?? [];
  const officialGoals = goals.filter((g) => g.framework !== 'CUSTOM');
  const customGoals = goals.filter((g) => g.framework === 'CUSTOM');
  // The edition in use = whatever framework the official goals carry.
  const edition = officialGoals[0]?.framework;

  // The aims available for the currently-selected theme, taken from the goals
  // already loaded (official + custom). Each option shows the aim's real
  // wording; its value is the aimNumber. This replaces the raw "Aim #" input.
  const aimsForTheme = React.useMemo(() => {
    const byNumber = new Map<number, string>();
    for (const g of goals) {
      if (g.theme === form.theme && !byNumber.has(g.aimNumber)) {
        byNumber.set(g.aimNumber, g.aimText);
      }
    }
    return [...byNumber.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([aimNumber, aimText]) => ({ aimNumber, aimText }));
  }, [goals, form.theme]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.aimNumber === null || !form.text.trim()) return;
    const aimText = aimsForTheme.find((a) => a.aimNumber === form.aimNumber)?.aimText;
    if (!aimText) return;
    // Next free goal index in this theme+aim, so a new custom goal never
    // collides with an existing one on the GOAL#theme#aim#index sort key.
    const usedIndexes = goals
      .filter((g) => g.theme === form.theme && g.aimNumber === form.aimNumber)
      .map((g) => g.goalIndex);
    const nextIndex = (usedIndexes.length ? Math.max(...usedIndexes) : 0) + 1;
    try {
      await createGoal({
        organizationId,
        theme: form.theme,
        aimNumber: form.aimNumber,
        aimText,
        goalIndex: nextIndex,
        text: form.text.trim(),
      }).unwrap();
      setForm({ ...EMPTY_FORM, theme: form.theme });
      showSuccess('Custom learning goal added.');
    } catch (err) {
      showError((err as any)?.data?.error || 'Failed to add learning goal.');
    }
  };

  const handleDeactivate = async (goal: LearningGoal) => {
    try {
      await updateGoal({ organizationId, goalId: goal.id, isActive: false }).unwrap();
      showSuccess('Learning goal deactivated.');
    } catch (err) {
      showError((err as any)?.data?.error || 'Failed to deactivate learning goal.');
    }
  };

  if (isLoading) return <p className="text-sm text-gray-500">Loading learning goals…</p>;
  if (isError) return <p className="text-sm text-rose-600">Failed to load learning goals.</p>;

  return (
    <div data-testid="learning-goals-admin" className="space-y-10">
      {/* ── Official Aistéar goals ────────────────────────────────────── */}
      <section data-testid="lg-aistear-section">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Aistéar learning goals</h2>
          {edition && (
            <span
              data-testid="lg-aistear-edition"
              className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800"
            >
              {EDITION_LABELS[edition]}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-500">
          The official NCCA Aistéar framework. These goals are fixed — tap a theme to see its aims
          and goals.
        </p>
        {officialGoals.length === 0 ? (
          <p className="mt-4 text-sm italic text-gray-400">No official goals loaded.</p>
        ) : (
          <ThemeCards goals={officialGoals} testIdPrefix="lg-aistear" />
        )}
      </section>

      {/* ── Custom goals ──────────────────────────────────────────────── */}
      <section data-testid="lg-custom-section" className="border-t border-gray-200 pt-8">
        <h2 className="text-lg font-semibold text-gray-900">Your custom goals</h2>
        <p className="mt-1 text-sm text-gray-500">
          Goals your organisation has added on top of the Aistéar framework.
          {!readOnly && ' Custom goals can be deactivated.'}
        </p>

        {!readOnly && (
          <form
            data-testid="lg-add-custom"
            onSubmit={handleAdd}
            className="mt-4 space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          >
            <h3 className="text-sm font-semibold text-gray-800">Add a custom goal</h3>

            <div>
              <label htmlFor="lg-custom-theme" className="block text-xs font-medium text-gray-600">
                Theme
              </label>
              <select
                id="lg-custom-theme"
                data-testid="lg-custom-theme"
                value={form.theme}
                onChange={(e) =>
                  // Changing theme clears the aim (its aims differ).
                  setForm((f) => ({
                    ...f,
                    theme: e.target.value as LearningGoalTheme,
                    aimNumber: null,
                  }))
                }
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {THEME_ORDER.map((theme) => (
                  <option key={theme} value={theme}>
                    {THEME_LABELS[theme]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="lg-custom-aim" className="block text-xs font-medium text-gray-600">
                Aim
                <span className="ml-1 font-normal text-gray-400">
                  — the part of this theme your goal supports
                </span>
              </label>
              <select
                id="lg-custom-aim"
                data-testid="lg-custom-aim"
                value={form.aimNumber ?? ''}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    aimNumber: e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Choose an aim…</option>
                {aimsForTheme.map((aim) => (
                  <option key={aim.aimNumber} value={aim.aimNumber}>
                    {aim.aimText}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="lg-custom-text" className="block text-xs font-medium text-gray-600">
                Goal
              </label>
              <textarea
                id="lg-custom-text"
                data-testid="lg-custom-text"
                placeholder="What will children be able to do? (e.g. Take turns during group games)"
                value={form.text}
                onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                required
                className="mt-1 block h-20 w-full resize-y rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                data-testid="lg-custom-submit"
                disabled={isCreating || form.aimNumber === null || !form.text.trim()}
                className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isCreating ? 'Saving…' : 'Add goal'}
              </button>
            </div>
          </form>
        )}

        {customGoals.length === 0 ? (
          <p data-testid="lg-custom-empty" className="mt-4 text-sm italic text-gray-400">
            No custom goals yet
            {!readOnly && ' — add one above to tailor the framework to your service'}.
          </p>
        ) : (
          <ThemeCards
            goals={customGoals}
            testIdPrefix="lg-custom"
            onDeactivate={readOnly ? undefined : handleDeactivate}
          />
        )}
      </section>
    </div>
  );
};

export default CustomGoalsAdmin;
